import * as RMenu from "@radix-ui/react-dropdown-menu";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactElement,
} from "react";

import { cn } from "../utils/cn";

/** Thin wrapper around Radix DropdownMenu that applies Vidario styles.
 * Consumers use the namespace: DropdownMenu.Root, .Trigger, .Content, .Item, ... */
export const DropdownMenu = {
  Root: RMenu.Root,
  Trigger: RMenu.Trigger,
  Portal: RMenu.Portal,
  Separator: forwardRef<
    ElementRef<typeof RMenu.Separator>,
    ComponentPropsWithoutRef<typeof RMenu.Separator>
  >(function Separator({ className, ...rest }, ref): ReactElement {
    return (
      <RMenu.Separator
        ref={ref}
        className={cn("my-1 h-px bg-border-default", className)}
        {...rest}
      />
    );
  }),
  Content: forwardRef<
    ElementRef<typeof RMenu.Content>,
    ComponentPropsWithoutRef<typeof RMenu.Content>
  >(function Content({ className, sideOffset = 6, align = "end", ...rest }, ref): ReactElement {
    return (
      <RMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        className={cn(
          "z-50 min-w-60 rounded-xl border border-border-default bg-neutral-0 p-1.5 shadow-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out",
          className,
        )}
        {...rest}
      />
    );
  }),
  Item: forwardRef<
    ElementRef<typeof RMenu.Item>,
    ComponentPropsWithoutRef<typeof RMenu.Item>
  >(function Item({ className, ...rest }, ref): ReactElement {
    return (
      <RMenu.Item
        ref={ref}
        className={cn(
          "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm text-neutral-700 outline-none",
          "data-[highlighted]:bg-primary-50 data-[highlighted]:text-primary-700",
          "data-[disabled]:cursor-default data-[disabled]:text-text-disabled data-[disabled]:data-[highlighted]:bg-transparent",
          className,
        )}
        {...rest}
      />
    );
  }),
  Label: forwardRef<
    ElementRef<typeof RMenu.Label>,
    ComponentPropsWithoutRef<typeof RMenu.Label>
  >(function Label({ className, ...rest }, ref): ReactElement {
    return (
      <RMenu.Label
        ref={ref}
        className={cn("px-3 py-2 text-xs font-semibold uppercase tracking-wider text-text-tertiary", className)}
        {...rest}
      />
    );
  }),
};
